import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  Tooltip,
  IconButton,
  CircularProgress,
  Button,
  Alert,
} from "@mui/material";
import {
  Equalizer,
  MonetizationOn,
  AssignmentLate,
  AssignmentTurnedIn,
  Business,
  LocationOn,
  CreditCard,
  Receipt,
  ExpandMore,
  ShowChart,
  PieChart,
  Print,
  Download,
  Email,
  Refresh,
  ArrowForward,
  Cancel,
  Pause,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";
import { format, subDays, isValid, parseISO, parse } from "date-fns";
import { BASE_URL } from "../../components/config/config";
import { styled } from "@mui/material/styles";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

const StatusChip = styled(Chip)(({ theme, status }) => {
  let backgroundColor, color;

  switch (status) {
    case "PENDING":
      backgroundColor = theme.palette.warning.light;
      color = theme.palette.warning.contrastText;
      break;
    case "ACCEPTED":
      backgroundColor = theme.palette.info.light;
      color = theme.palette.info.contrastText;
      break;
    case "CLOSED":
      backgroundColor = theme.palette.success.light;
      color = theme.palette.success.contrastText;
      break;
    case "CANCEL":
      backgroundColor = theme.palette.error.light;
      color = theme.palette.error.contrastText;
      break;
    case "ON_HOLD":
      backgroundColor = theme.palette.grey[500];
      color = theme.palette.grey[50];
      break;
    default:
      backgroundColor = theme.palette.grey[300];
      color = theme.palette.text.primary;
  }

  return {
    backgroundColor,
    color,
    fontWeight: 600,
    minWidth: 90,
  };
});

const ClientReport = () => {
  const clientState = useSelector((state) => state.clientReducer);
  const clientId = clientState.clientDetails?._id;
  const clientName = clientState.clientDetails?.client_name;
  const clientCode = clientState.clientDetails?.client_id;

  // Time period filter
  const [timePeriod, setTimePeriod] = useState("week");
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Report data state
  const [reportData, setReportData] = useState({
    clientDetails: null,
    ticketSummary: {
      total_tickets: 0,
      tickets_by_status: {
        PENDING: 0,
        ACCEPTED: 0,
        CLOSED: 0,
        CANCEL: 0,
        ON_HOLD: 0,
      },
    },
    walletSummary: {
      wallet_balance: 0,
      total_credited_amount: 0,
      total_debit_amount: 0,
      total_profit: 0,
    },
    transactionList: [],
  });

  // Chart data states
  const [ticketStatusData, setTicketStatusData] = useState([]);
  const [financialTrendData, setFinancialTrendData] = useState([]);
  const [transactionActivityData, setTransactionActivityData] = useState([]);

  // Time period options
  const timePeriods = [
    { value: "day", label: "Today" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "quarter", label: "Last Quarter" },
    { value: "half-year", label: "Last 6 Months" },
    { value: "year", label: "This Year" },
    { value: "custom", label: "Custom Range" },
  ];

  // Colors for charts
  const PIE_CHART_COLORS = [
    "#0088FE", // PENDING - Blue
    "#00C49F", // CLOSED - Green
    "#FFBB28", // ACCEPTED - Yellow
    "#FF8042", // CANCEL - Orange
    "#8884D8", // ON_HOLD - Purple
  ];

  // Validate and format date for API
  const formatDateForAPI = (date) => {
    if (!date || !isValid(date)) {
      return format(new Date(), "dd/MM/yyyy");
    }
    return format(date, "dd/MM/yyyy");
  };

  // Parse date from API response
  const parseDateFromAPI = (dateString) => {
    if (!dateString) return null;
    try {
      const isoDate = parseISO(dateString);
      if (isValid(isoDate)) return isoDate;

      const parsedDate = parse(dateString, "dd/MM/yyyy", new Date());
      if (isValid(parsedDate)) return parsedDate;

      return new Date();
    } catch (error) {
      console.error("Error parsing date:", error);
      return new Date();
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchReportData();
    } else {
      setError("Client ID not available");
      setLoading(false);
    }
  }, [clientId, timePeriod, startDate, endDate]);

  // Prepare chart data when reportData changes
  useEffect(() => {
    if (reportData.ticketSummary.tickets_by_status) {
      prepareTicketStatusData();
    }
    if (reportData.transactionList.length > 0) {
      prepareFinancialTrendData();
      prepareTransactionActivityData();
    }
  }, [reportData]);

  const prepareTicketStatusData = () => {
    const { tickets_by_status } = reportData.ticketSummary;
    const data = [
      {
        name: "Pending",
        value: tickets_by_status.PENDING || 0,
        status: "PENDING",
      },
      {
        name: "Closed",
        value: tickets_by_status.CLOSED || 0,
        status: "CLOSED",
      },
      {
        name: "Accepted",
        value: tickets_by_status.ACCEPTED || 0,
        status: "ACCEPTED",
      },
      {
        name: "Canceled",
        value: tickets_by_status.CANCEL || 0,
        status: "CANCEL",
      },
      {
        name: "On Hold",
        value: tickets_by_status.ON_HOLD || 0,
        status: "ON_HOLD",
      },
    ].filter((item) => item.value > 0);

    setTicketStatusData(data);
  };

  const prepareFinancialTrendData = () => {
    // Group transactions by month for trend analysis
    const monthlyData = {};

    reportData.transactionList.forEach((transaction) => {
      if (transaction.createdAt) {
        const monthYear = format(transaction.createdAt, "MMM yyyy");
        if (!monthlyData[monthYear]) {
          monthlyData[monthYear] = {
            month: monthYear,
            credited: 0,
            debited: 0,
            profit: 0,
          };
        }

        if (transaction.transaction_type === "ADDED") {
          monthlyData[monthYear].credited += transaction.amount || 0;
        } else if (transaction.transaction_type === "DEDUCTED") {
          monthlyData[monthYear].debited += transaction.amount || 0;
        }

        monthlyData[monthYear].profit =
          monthlyData[monthYear].credited - monthlyData[monthYear].debited;
      }
    });

    const data = Object.values(monthlyData).sort((a, b) => {
      const [aMonth, aYear] = a.month.split(" ");
      const [bMonth, bYear] = b.month.split(" ");
      const dateA = new Date(`${aMonth} 1, ${aYear}`);
      const dateB = new Date(`${bMonth} 1, ${bYear}`);
      return dateA - dateB;
    });

    setFinancialTrendData(data);
  };

  const prepareTransactionActivityData = () => {
    // Group transactions by day for activity chart
    const dailyData = {};
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = subDays(new Date(), i);
      return format(date, "dd MMM");
    }).reverse();

    // Initialize with zeros
    last7Days.forEach((day) => {
      dailyData[day] = {
        day,
        transactions: 0,
        amount: 0,
      };
    });

    // Fill with actual data
    reportData.transactionList.forEach((transaction) => {
      if (transaction.createdAt) {
        const day = format(transaction.createdAt, "dd MMM");
        if (dailyData[day]) {
          dailyData[day].transactions += 1;
          dailyData[day].amount += transaction.amount || 0;
        }
      }
    });

    const data = Object.values(dailyData);
    setTransactionActivityData(data);
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!clientId) {
        throw new Error("Client ID is not available");
      }

      const formattedStartDate = formatDateForAPI(startDate);
      const formattedEndDate = formatDateForAPI(endDate);

      console.log("Fetching report for client:", {
        clientId,
        displayDateRange: `${formattedStartDate} to ${formattedEndDate}`,
      });

      const accessToken = clientState.userTokens?.accessToken;
      if (!accessToken) {
        throw new Error("Authentication token not found");
      }

      const response = await axios.get(
        `${BASE_URL}client/get-client-report/${clientId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      console.log("API Response:", response);

      if (!response) {
        throw new Error("No response received from server");
      }

      if (!response.data) {
        throw new Error("Invalid response format - no data received");
      }

      if (response.status >= 400) {
        throw new Error(
          `Server error: ${response.status} ${response.statusText}`,
        );
      }

      const apiData = response.data;

      if (apiData.success === false) {
        throw new Error(apiData.message || "API request failed");
      }

      const reportData = apiData.data || apiData;

      if (!reportData) {
        throw new Error("No report data received from server");
      }

      // Process the data according to API response structure
      const processedTransactions = (reportData.transaction_list || []).map(
        (transaction) => ({
          ...transaction,
          createdAt: transaction.createdAt
            ? parseDateFromAPI(transaction.createdAt)
            : null,
        }),
      );

      // Process ticket summary - ensure all statuses are included
      const ticketByStatus = reportData.ticket_summary?.tickets_by_status || {};
      const processedTicketSummary = {
        total_tickets: reportData.ticket_summary?.total_tickets || 0,
        tickets_by_status: {
          PENDING: ticketByStatus.PENDING || 0,
          ACCEPTED: ticketByStatus.ACCEPTED || 0,
          CLOSED: ticketByStatus.CLOSED || 0,
          CANCEL: ticketByStatus.CANCEL || 0,
          ON_HOLD: ticketByStatus.ON_HOLD || 0,
        },
      };

      // Process wallet summary
      const processedWalletSummary = {
        wallet_balance: reportData.wallet_summary?.wallet_balance || 0,
        total_credited_amount:
          reportData.wallet_summary?.total_credited_amount || 0,
        total_debit_amount: reportData.wallet_summary?.total_debit_amount || 0,
        total_profit: reportData.wallet_summary?.total_profit || 0,
      };

      setReportData({
        clientDetails: reportData.client_details || clientState.clientDetails,
        ticketSummary: processedTicketSummary,
        walletSummary: processedWalletSummary,
        transactionList: processedTransactions,
      });

      setLoading(false);
    } catch (err) {
      console.error("Error fetching report data:", err);

      let errorMessage = "Failed to load report data. Please try again.";

      if (err.response) {
        const serverError = err.response.data;
        errorMessage =
          serverError?.message ||
          serverError?.error ||
          `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage =
          "No response received from server. Please check your connection.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setLoading(false);

      setReportData({
        clientDetails: clientState.clientDetails,
        ticketSummary: {
          total_tickets: 0,
          tickets_by_status: {
            PENDING: 0,
            ACCEPTED: 0,
            CLOSED: 0,
            CANCEL: 0,
            ON_HOLD: 0,
          },
        },
        walletSummary: {
          wallet_balance: 0,
          total_credited_amount: 0,
          total_debit_amount: 0,
          total_profit: 0,
        },
        transactionList: [],
      });
    }
  };

  const handleTimePeriodChange = (event) => {
    const newPeriod = event.target.value;
    setTimePeriod(newPeriod);

    const now = new Date();
    switch (newPeriod) {
      case "day":
        setStartDate(subDays(now, 1));
        setEndDate(now);
        break;
      case "week":
        setStartDate(subDays(now, 7));
        setEndDate(now);
        break;
      case "month":
        setStartDate(subDays(now, 30));
        setEndDate(now);
        break;
      case "quarter":
        setStartDate(subDays(now, 90));
        setEndDate(now);
        break;
      case "half-year":
        setStartDate(subDays(now, 180));
        setEndDate(now);
        break;
      case "year":
        setStartDate(subDays(now, 365));
        setEndDate(now);
        break;
      case "custom":
        break;
      default:
        setStartDate(subDays(now, 7));
        setEndDate(now);
    }
  };

  const calculateCompletionRate = () => {
    const { total_tickets, tickets_by_status } = reportData.ticketSummary;
    if (total_tickets === 0) return 0;

    const completedTickets = tickets_by_status.CLOSED || 0;
    return (completedTickets / total_tickets) * 100;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDisplayDate = (date) => {
    if (!date || !isValid(date)) return "N/A";
    return format(date, "dd MMM yyyy");
  };

  const formatDisplayDateTime = (date) => {
    if (!date || !isValid(date)) return "N/A";
    return format(date, "dd MMM yyyy HH:mm");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "PENDING":
        return <AssignmentLate />;
      case "ACCEPTED":
        return <AssignmentTurnedIn />;
      case "CLOSED":
        return <AssignmentTurnedIn />;
      case "CANCEL":
        return <Cancel />;
      case "ON_HOLD":
        return <Pause />;
      default:
        return <AssignmentLate />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "ACCEPTED":
        return "info";
      case "CLOSED":
        return "success";
      case "CANCEL":
        return "error";
      case "ON_HOLD":
        return "default";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading client report...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
          flexDirection: "column",
        }}
      >
        <Alert
          severity="error"
          sx={{ mb: 2, width: "100%", maxWidth: 600 }}
          action={
            <Button color="inherit" size="small" onClick={fetchReportData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={fetchReportData}
          startIcon={<Refresh />}
        >
          Refresh Report
        </Button>
      </Box>
    );
  }

  if (!clientId) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <Typography variant="h6">
          Please select a client to view the report
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "primary.main" }}
            >
              {clientName || "Client"} Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Client ID: {clientCode || "N/A"} | {formatDisplayDate(startDate)}{" "}
              - {formatDisplayDate(endDate)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Print Report">
              <IconButton sx={{ backgroundColor: "action.hover" }}>
                <Print />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download PDF">
              <IconButton sx={{ backgroundColor: "action.hover" }}>
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title="Email Report">
              <IconButton sx={{ backgroundColor: "action.hover" }}>
                <Email />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filters Section */}
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Time Period</InputLabel>
                  <Select
                    value={timePeriod}
                    label="Time Period"
                    onChange={handleTimePeriodChange}
                  >
                    {timePeriods.map((period) => (
                      <MenuItem key={period.value} value={period.value}>
                        {period.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {timePeriod === "custom" && (
                <>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(newValue) => {
                        if (newValue && isValid(newValue)) {
                          setStartDate(newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                      maxDate={endDate}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(newValue) => {
                        if (newValue && isValid(newValue)) {
                          setEndDate(newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                      minDate={startDate}
                      maxDate={new Date()}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={fetchReportData}
                  startIcon={<Refresh />}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabs Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="report tabs"
          >
            <Tab label="Overview" icon={<ShowChart />} iconPosition="start" />
            <Tab label="Transactions" icon={<Receipt />} iconPosition="start" />
            <Tab
              label="Client Details"
              icon={<Business />}
              iconPosition="start"
            />
            <Tab label="Analytics" icon={<PieChart />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Box>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Total Tickets
                      </Typography>
                      <Avatar
                        sx={{
                          bgcolor: "primary.light",
                          color: "primary.contrastText",
                        }}
                      >
                        <Equalizer />
                      </Avatar>
                    </Box>
                    <Typography variant="h4" sx={{ mt: 1, fontWeight: 700 }}>
                      {reportData.ticketSummary.total_tickets}
                    </Typography>
                    <Box display="flex" alignItems="center" mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        All service requests
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Pending Tickets
                      </Typography>
                      <Avatar
                        sx={{
                          bgcolor: "warning.light",
                          color: "warning.contrastText",
                        }}
                      >
                        <AssignmentLate />
                      </Avatar>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{ mt: 1, fontWeight: 700, color: "warning.main" }}
                    >
                      {reportData.ticketSummary.tickets_by_status.PENDING}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {reportData.ticketSummary.total_tickets > 0
                        ? `${(
                            (reportData.ticketSummary.tickets_by_status
                              .PENDING /
                              reportData.ticketSummary.total_tickets) *
                            100
                          ).toFixed(1)}% of total`
                        : "0% of total"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Completion Rate
                      </Typography>
                      <Avatar
                        sx={{
                          bgcolor: "success.light",
                          color: "success.contrastText",
                        }}
                      >
                        <AssignmentTurnedIn />
                      </Avatar>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{ mt: 1, fontWeight: 700, color: "success.main" }}
                    >
                      {calculateCompletionRate().toFixed(1)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={calculateCompletionRate()}
                      sx={{ height: 8, borderRadius: 4, mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Wallet Balance
                      </Typography>
                      <Avatar
                        sx={{
                          bgcolor: "info.light",
                          color: "info.contrastText",
                        }}
                      >
                        <MonetizationOn />
                      </Avatar>
                    </Box>
                    <Typography variant="h4" sx={{ mt: 1, fontWeight: 700 }}>
                      {formatCurrency(reportData.walletSummary.wallet_balance)}
                    </Typography>
                    <Box mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        Profit:{" "}
                        {formatCurrency(reportData.walletSummary.total_profit)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Detailed Cards Row */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, display: "flex", alignItems: "center" }}
                    >
                      <MonetizationOn color="primary" sx={{ mr: 1 }} />{" "}
                      Financial Summary
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: "success.light",
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="success.contrastText"
                          >
                            Total Credited
                          </Typography>
                          <Typography variant="h6" color="success.contrastText">
                            {formatCurrency(
                              reportData.walletSummary.total_credited_amount,
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: "error.light",
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="error.contrastText"
                          >
                            Total Debited
                          </Typography>
                          <Typography variant="h6" color="error.contrastText">
                            {formatCurrency(
                              reportData.walletSummary.total_debit_amount,
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: "primary.light",
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="primary.contrastText"
                          >
                            Net Profit
                          </Typography>
                          <Typography variant="h5" color="primary.contrastText">
                            {formatCurrency(
                              reportData.walletSummary.total_profit,
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, display: "flex", alignItems: "center" }}
                    >
                      <Equalizer color="primary" sx={{ mr: 1 }} /> Ticket Status
                      Breakdown
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {Object.entries(
                        reportData.ticketSummary.tickets_by_status,
                      )
                        .filter(([_, count]) => count > 0)
                        .map(([status, count]) => (
                          <Grid item xs={4} key={status}>
                            <Box sx={{ textAlign: "center" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                {getStatusIcon(status)}
                              </Box>
                              <StatusChip
                                label={status.replace("_", " ")}
                                status={status}
                                size="small"
                              />
                              <Typography variant="h5" sx={{ mt: 1 }}>
                                {count}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {reportData.ticketSummary.total_tickets > 0
                                  ? `${(
                                      (count /
                                        reportData.ticketSummary
                                          .total_tickets) *
                                      100
                                    ).toFixed(1)}%`
                                  : "0%"}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      <Grid item xs={12}>
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress
                            variant="determinate"
                            value={calculateCompletionRate()}
                            sx={{ height: 10, borderRadius: 5 }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mt: 1,
                            }}
                          >
                            <Typography variant="caption">Pending</Typography>
                            <Typography variant="caption">
                              {calculateCompletionRate().toFixed(1)}% Completed
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Recent Transactions Preview */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Receipt color="primary" sx={{ mr: 1 }} /> Recent
                    Transactions
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setActiveTab(1)}
                    endIcon={<ArrowForward />}
                  >
                    View All
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.transactionList.length > 0 ? (
                        reportData.transactionList
                          .slice(0, 5)
                          .map((transaction) => (
                            <TableRow key={transaction._id || Math.random()}>
                              <TableCell>
                                {formatDisplayDate(transaction.createdAt)}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {transaction.description || "N/A"}
                                </Typography>
                                {transaction.performed_by && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    By: {transaction.performed_by}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={transaction.transaction_type || "N/A"}
                                  size="small"
                                  color={
                                    transaction.transaction_type === "ADDED"
                                      ? "success"
                                      : "error"
                                  }
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  color={
                                    transaction.transaction_type === "ADDED"
                                      ? "success.main"
                                      : "error.main"
                                  }
                                  fontWeight={500}
                                >
                                  {transaction.transaction_type === "ADDED"
                                    ? "+"
                                    : "-"}
                                  {formatCurrency(transaction.amount || 0)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No transactions found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        )}

        {activeTab === 1 && (
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 2, display: "flex", alignItems: "center" }}
              >
                <Receipt color="primary" sx={{ mr: 1 }} /> Transaction History
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "action.hover" }}>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Performed By</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.transactionList.length > 0 ? (
                      reportData.transactionList.map((transaction) => (
                        <TableRow key={transaction._id || Math.random()} hover>
                          <TableCell>
                            {formatDisplayDateTime(transaction.createdAt)}
                          </TableCell>
                          <TableCell>
                            {transaction.description || "N/A"}
                          </TableCell>
                          <TableCell>
                            {transaction.performed_by || "System"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.transaction_type || "N/A"}
                              color={
                                transaction.transaction_type === "ADDED"
                                  ? "success"
                                  : "error"
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              fontWeight={500}
                              color={
                                transaction.transaction_type === "ADDED"
                                  ? "success.main"
                                  : "error.main"
                              }
                            >
                              {transaction.transaction_type === "ADDED"
                                ? "+"
                                : "-"}
                              {formatCurrency(transaction.amount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(transaction.remaining_balance || 0)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body1" color="text.secondary">
                            No transactions found for the selected period
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && reportData.clientDetails && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, display: "flex", alignItems: "center" }}
                  >
                    <Business color="primary" sx={{ mr: 1 }} /> Client
                    Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Client Name"
                        secondary={
                          reportData.clientDetails.client_name || "N/A"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Client ID"
                        secondary={reportData.clientDetails.client_id || "N/A"}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Short Code"
                        secondary={reportData.clientDetails.short_code || "N/A"}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Official Email"
                        secondary={
                          reportData.clientDetails.official_email || "N/A"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="GST Number"
                        secondary={reportData.clientDetails.gst_number || "N/A"}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="PAN Number"
                        secondary={reportData.clientDetails.pan_number || "N/A"}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="TIN Number"
                        secondary={reportData.clientDetails.tin_number || "N/A"}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, display: "flex", alignItems: "center" }}
                  >
                    <LocationOn color="primary" sx={{ mr: 1 }} /> Address
                    Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {reportData.clientDetails.address_details ? (
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Address"
                          secondary={
                            reportData.clientDetails.address_details
                              .address_line || "N/A"
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="City"
                          secondary={
                            reportData.clientDetails.address_details.city ||
                            "N/A"
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="State"
                          secondary={
                            reportData.clientDetails.address_details.state ||
                            "N/A"
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Country"
                          secondary={
                            reportData.clientDetails.address_details.country ||
                            "N/A"
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Pincode"
                          secondary={
                            reportData.clientDetails.address_details.pincode ||
                            "N/A"
                          }
                        />
                      </ListItem>
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No address details available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, display: "flex", alignItems: "center" }}
                  >
                    <CreditCard color="primary" sx={{ mr: 1 }} /> Service Areas
                    & Categories
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {reportData.clientDetails.serviceAreas ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography>Service Areas</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2">
                                States:
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mt: 1,
                                }}
                              >
                                {reportData.clientDetails.serviceAreas.states?.map(
                                  (state, index) => (
                                    <Chip key={index} label={state} />
                                  ),
                                ) || (
                                  <Typography variant="body2">
                                    No states specified
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2">
                                Cities:
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mt: 1,
                                }}
                              >
                                {reportData.clientDetails.serviceAreas.cities?.map(
                                  (city, index) => (
                                    <Chip key={index} label={city} />
                                  ),
                                ) || (
                                  <Typography variant="body2">
                                    No cities specified
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2">
                                Pincodes:
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mt: 1,
                                }}
                              >
                                {reportData.clientDetails.serviceAreas.pincodes?.map(
                                  (pincode, index) => (
                                    <Chip key={index} label={pincode} />
                                  ),
                                ) || (
                                  <Typography variant="body2">
                                    No pincodes specified
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography>Service Categories</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {reportData.clientDetails.serviceAreas.category_details?.map(
                              (category, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                  <Typography variant="subtitle1">
                                    {category.label} {category.label2}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Payment Method:{" "}
                                    {category.payment_way?.isPayViaClient
                                      ? "Pay via Client"
                                      : "Pay via Customer"}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Ticket Price: ₹
                                    {category.payment_way?.ticketPicPrize || 0}
                                  </Typography>
                                </Box>
                              ),
                            ) || (
                              <Typography variant="body2">
                                No categories specified
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No service area details available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {activeTab === 3 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Ticket Status Distribution
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Box sx={{ height: 300 }}>
                    {ticketStatusData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={ticketStatusData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={(entry) => `${entry.name}: ${entry.value}`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {ticketStatusData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  PIE_CHART_COLORS[
                                    index % PIE_CHART_COLORS.length
                                  ]
                                }
                              />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            formatter={(value, name) => [
                              value,
                              name === "value" ? "Tickets" : name,
                            ]}
                          />
                          <Legend />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          backgroundColor: "action.hover",
                          borderRadius: 2,
                        }}
                      >
                        <Typography color="text.secondary">
                          No ticket data available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Financial Trends
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Box sx={{ height: 300 }}>
                    {financialTrendData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={financialTrendData}
                          margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <RechartsTooltip
                            formatter={(value) => [
                              formatCurrency(value),
                              "Amount",
                            ]}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="credited"
                            stroke="#82ca9d"
                            name="Credited"
                            strokeWidth={2}
                          />
                          <Line
                            type="monotone"
                            dataKey="debited"
                            stroke="#ff8042"
                            name="Debited"
                            strokeWidth={2}
                          />
                          <Line
                            type="monotone"
                            dataKey="profit"
                            stroke="#8884d8"
                            name="Profit"
                            strokeWidth={3}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          backgroundColor: "action.hover",
                          borderRadius: 2,
                        }}
                      >
                        <Typography color="text.secondary">
                          No financial data available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Transaction Activity (Last 7 Days)
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Box sx={{ height: 400 }}>
                    {transactionActivityData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={transactionActivityData}
                          margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="day" />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" />
                          <RechartsTooltip
                            formatter={(value, name) => {
                              if (name === "amount") {
                                return [formatCurrency(value), "Amount"];
                              }
                              return [value, "Transactions"];
                            }}
                          />
                          <Legend />
                          <Bar
                            yAxisId="left"
                            dataKey="transactions"
                            name="Number of Transactions"
                            fill="#8884d8"
                          />
                          <Bar
                            yAxisId="right"
                            dataKey="amount"
                            name="Total Amount"
                            fill="#82ca9d"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          backgroundColor: "action.hover",
                          borderRadius: 2,
                        }}
                      >
                        <Typography color="text.secondary">
                          No transaction activity data available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default ClientReport;
